import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogs } from '../../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const blogsPerPage = 9;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offset = (page - 1) * blogsPerPage;
        const response = await fetchBlogs(blogsPerPage, offset);
        
        if (response.blogs) {
          setBlogs(response.blogs);
          setTotalBlogs(response.blogCount);
        } else {
          toast.warning('No blog posts found');
        }
      } catch (err) {
        toast.error('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page]);

  const totalPages = Math.ceil(totalBlogs / blogsPerPage);

  const getThumbnailImage = (blog) => {
    if (blog.thumbnail_image) {
      return blog.thumbnail_image;
    }
    return '/assets/images/error.jpeg';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
          Our Blog
        </h1>
        <p className="mt-4 text-xl text-gray-500">
          Latest updates, news, and insights from our team
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {blogs.map((blog) => (
          <div key={blog.post_id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
            <div className="relative h-48">
              <img
                src={getThumbnailImage(blog)}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-6">
              {blog.created_at && (
                <div className="text-sm text-gray-500 mb-2">
                  {new Date(blog.created_at).toLocaleDateString()}
                </div>
              )}
              <h2 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                {blog.title}
              </h2>
              {blog.excerpt && (
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {blog.excerpt}
                </p>
              )}
              <Link
                to={`/blog/${blog.post_id}`}
                className="text-green-600 hover:text-green-700 font-medium inline-flex items-center"
                onClick={(e) => {
                  if (!blog.post_id) {
                    e.preventDefault();
                    toast.error('Invalid blog post ID');
                  }
                }}
              >
                Read More →
              </Link>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-12 flex justify-center space-x-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => setPage(pageNum)}
              className={`px-4 py-2 rounded-md ${
                page === pageNum
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {pageNum}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogPage; 